import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import ListItemText from '@mui/material/ListItemText';
import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';

import { toast } from 'sonner';
import { useState } from 'react';

import { Label } from 'src/components/label';
import { Iconify } from 'src/components/iconify';
import { usePopover, CustomPopover } from 'src/components/custom-popover';
import { MembersManagementDialog } from '../activity/manage-members-modal';
import {
  mark_as_active,
  reject_delete_request,
  send_changes_for_approval,
  send_request_to_members,
} from 'src/auth/context/jwt/program';
import { CircularProgress } from '@mui/material';
import { paths } from 'src/routes/paths';
import { useNavigate } from 'react-router';
import { useAuthContext } from 'src/auth/hooks';

// ----------------------------------------------------------------------

export function MemberActivityCard({
  activity,
  onAssignMembers,
  onUnassignMember,
  onDelete,
  onRelease,
  onEdit,
  allowUnassign = true,
  sx,
  onRefresh,
  ...other
}) {
  const popover = usePopover();
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const { user } = useAuthContext();
  // Get status color based on activity status
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'draft':
        return 'success';
      case 'approval_requested':
        return 'info';
      case 'approved':
        return 'info';
      case 'changes_requested':
        return 'error';
      case 'deleted':
        return 'error';
      case 'active':
        return 'success';
      case 'completed':
        return 'primary';
      default:
        return 'default';
    }
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Get assigned members
  const assignedMembers = activity?.members || [];

  const [loading, setloading] = useState(false);
  // Makr As Active
  const markAsActive = async (program_id) => {
    setloading(true);
    try {
      // Call the parent component's handler
      const res = await mark_as_active({ program_id });
      popover.onClose();
      toast.success(res?.message);
      onRefresh();
    } catch (error) {
      console.error('Error marking active:', error);
    } finally {
      setloading(false);
    }
  };

  const [approval_loading, setapproval_loading] = useState(false);

  const send_change_for_approval = async (id) => {
    setapproval_loading(true);
    try {
      const res = await send_changes_for_approval({
        program_id: id || activity?.id,
      });
      toast.success(res?.message);
      onRefresh();
    } catch (e) {
      console.log(e);
    } finally {
      setapproval_loading(false);
    }
  };

  const isEditable =
    activity?.status !== 'approval_requested' &&
    activity?.status !== 'completed' &&
    activity?.status !== 'deleted' &&
    activity?.child_program === null;
  const isRequest = activity?.status === 'draft';
  const isMarkActive = activity?.status === 'approved';

  const isViewable = true;

  const isEditableChild =
    activity?.child_program?.status !== 'approval_requested' &&
    activity?.child_program?.status !== 'completed';
  const isRequestChild = activity?.child_program?.status === 'draft';
  const isMarkActiveChild = activity?.child_program?.status === 'approved';

  const returnStatus = (status) => {
    return status.replace('_', ' ');
  };

  const returnChildState = (status) => {
    switch (status?.toLowerCase()) {
      case 'draft':
        return 'New Changes are drafted';
      case 'approval_requested':
        return 'New Changes are pending approval';
      case 'approved':
        return 'New Changes have been approved by member';
      case 'changes_requested':
        return 'New Changes are requested by member';
      case 'active':
        return '';
      case 'completed':
        return '';
      default:
        return '';
    }
  };

  const navigate = useNavigate();

  const [rejecting, setrejecting] = useState(false);
  const rejectDeleteRequest = async () => {
    try {
      setrejecting(true);
      const res = await reject_delete_request({
        id: activity?.delete_request_id,
      });
      popover.onClose();
      onRefresh();
    } catch (e) {
      console.log(e);
    } finally {
      setrejecting(false);
    }
  };

  return (
    <>
      <Card sx={{ textAlign: 'center', position: 'relative', ...sx }} {...other}>
        {/* Action Menu Button */}
        <IconButton
          onClick={popover.onOpen}
          sx={{
            position: 'absolute',
            top: 8,
            right: 2,
            zIndex: 20,
            color: 'default',
          }}
        >
          <Iconify icon="eva:more-vertical-fill" />
        </IconButton>

        {/* Status Label */}
        <Label
          sx={{
            textTransform: 'capitalize',
            position: 'absolute',
            top: 15,
            left: 10,
            zIndex: 10,
          }}
          variant="soft"
          color={getStatusColor(activity.status)}
        >
          {returnStatus(activity.status)}
        </Label>

        {/* Activity Icon */}
        <Box sx={{ position: 'relative', mt: 4 }}>
          <Avatar
            sx={{
              width: 64,
              height: 64,
              zIndex: 11,
              mt: 2,
              mx: 'auto',
              bgcolor: 'primary.main',
            }}
            src={activity?.member?.avatar}
            alt={activity?.member?.name}
          >
            {/* <Iconify icon="material-symbols:fitness-center" width={32} /> */}
          </Avatar>
        </Box>

        {/* Activity Title and Start Date */}
        <ListItemText
          sx={{ mt: 1, mb: 1.5, px: 2 }}
          primary={activity?.member?.name}
          secondary={`Start Date: ${formatDate(activity?.start_date)}`}
          primaryTypographyProps={{
            typography: 'subtitle1',
            fontWeight: 600,
            noWrap: true,
          }}
          secondaryTypographyProps={{
            component: 'span',
            mt: 0.5,
            color: 'text.secondary',
            typography: 'body2',
          }}
        />

        <Divider sx={{ borderStyle: 'dashed' }} />

        {/* Activity Details */}
        <Box
          sx={{
            display: 'flex',
            py: 1.5,
            px: 2,
            justifyContent: 'space-around',
            alignItems: 'center',
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="caption" component="div" sx={{ mb: 0.5, color: 'text.secondary' }}>
              Duration
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {activity.duration} {activity.duration === 1 ? 'Week' : 'Weeks'}
            </Typography>
          </Box>

          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="caption" component="div" sx={{ mb: 0.5, color: 'text.secondary' }}>
              Workouts
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {activity?.exercise_count || 'N/A'}
            </Typography>
          </Box>
        </Box>

        {/* CHILD PROGRAM CARD */}
        {activity?.child_program !== null && (
          <Box
            sx={{
              bgcolor: (theme) =>
                theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'grey.200',
              borderRadius: 2,
              p: 0.5,
              overflowY: 'auto',
              border: '1px solid #e0e0e0',
              mb: 2,
              mx: 2,
            }}
          >
            <Typography my={1}>{returnChildState(activity?.child_program?.status)}</Typography>

            {isEditableChild && (
              <MenuItem
                sx={{ justifyContent: 'center', textDecorationLine: 'underline' }}
                disabled={loading}
                onClick={() => {
                  navigate(`${paths.dashboard.general.manageActivity}`, {
                    state: {
                      activity_id: activity?.child_program?.id,
                      type: 'edit',
                    },
                  });
                }}
              >
                View & Edit
              </MenuItem>
            )}

            {isRequestChild && (
              <MenuItem
                sx={{ justifyContent: 'center', textDecorationLine: 'underline' }}
                disabled={loading}
                onClick={() => {
                  send_change_for_approval(activity?.child_program?.id);
                }}
              >
                {approval_loading ? <CircularProgress color="success" size={18} /> : null}
                Request approval
              </MenuItem>
            )}

            {isMarkActiveChild && (
              <MenuItem
                sx={{
                  justifyContent: 'center',
                  textDecorationLine: 'underline',
                  color: 'success.main',
                }}
                disabled={loading}
                onClick={() => {
                  markAsActive(activity?.child_program?.id);
                }}
              >
                {loading ? <CircularProgress color="success" size={18} /> : null}
                Mark Active
              </MenuItem>
            )}
          </Box>
        )}
      </Card>

      {/* Action Menu Popover */}
      <CustomPopover
        open={popover.open}
        anchorEl={popover.anchorEl}
        onClose={popover.onClose}
        slotProps={{ arrow: { placement: 'right-top' } }}
      >
        <MenuList>
          {isViewable && (
            <MenuItem
              onClick={() => {
                popover.onClose();
                navigate(`${paths.dashboard.general.viewProgram}`, {
                  state: { activity_id: activity?.id, type: 'view', is_trail: activity?.is_trail },
                });
              }}
            >
              <Iconify icon="lets-icons:view" />
              View Program
            </MenuItem>
          )}
          {isEditable && (
            <MenuItem
              onClick={() => {
                popover.onClose();
                onEdit && onEdit(activity);
              }}
            >
              <Iconify icon="solar:pen-bold" />
              Edit Program
            </MenuItem>
          )}

          {isRequest && (
            <MenuItem
              disabled={loading}
              onClick={() => {
                send_change_for_approval();
              }}
              // sx={{ color: 'success.main' }}
            >
              {approval_loading ? (
                <CircularProgress color="success" size={18} />
              ) : (
                <Iconify icon="material-symbols:send" />
              )}
              Request approval
            </MenuItem>
          )}

          {isMarkActive && (
            <MenuItem
              disabled={loading}
              onClick={() => {
                markAsActive(activity?.id);
                // setAssignModalOpen(true);
              }}
              sx={{ color: 'success.main' }}
            >
              {loading ? (
                <CircularProgress color="success" size={18} />
              ) : (
                <Iconify icon="charm:circle-tick" />
              )}
              Mark Active
            </MenuItem>
          )}

          {activity?.status === 'active' && (
            <MenuItem
              onClick={() => {
                onRelease && onRelease(activity);
              }}
              sx={{ color: 'info.main' }}
            >
              <Iconify icon="material-symbols:logout" />
              Release Program
            </MenuItem>
          )}

          {activity?.status !== 'deleted' && (
            <MenuItem
              disabled={
                (activity?.delete_request === 1 &&
                  activity?.delete_request_by !== user?.role?.id) ||
                rejecting
              }
              onClick={() => {
                if (onDelete && activity?.delete_request === 0) {
                  popover.onClose();
                  onDelete(activity);
                }
                activity?.delete_request_by === user?.role?.id && rejectDeleteRequest();
              }}
              sx={{ color: 'error.main' }}
            >
              <Iconify icon="solar:trash-bin-trash-bold" />
              {activity?.delete_request === 1 &&
                activity?.delete_request_by === user?.role?.id &&
                'Cancel request'}
              {activity?.delete_request === 1 &&
                activity?.delete_request_by !== user?.role?.id &&
                'Delete requested by member'}
              {activity?.delete_request === 0 && 'Request delete program'}
            </MenuItem>
          )}

          {/* {activity?.status === 'active' && (
            <MenuItem
              onClick={() => {
                onRelease && onRelease(activity);
              }}
              sx={{ color: 'info.main' }}
            >
              <Iconify icon="material-symbols:logout" />
              Release Program
            </MenuItem>
          )}

          {activity?.status !== 'deleted' && (
            <MenuItem
              disabled={
                (activity?.delete_request === 1 &&
                  activity?.delete_request_by !== user?.role?.id) ||
                rejecting
              }
              onClick={() => {
                if (onDelete && activity?.delete_request === 0) {
                  popover.onClose();
                  onDelete(activity);
                }
                activity?.delete_request_by === user?.role?.id && rejectDeleteRequest();
              }}
              sx={{ color: 'error.main' }}
            >
              <Iconify icon="solar:trash-bin-trash-bold" />
              {activity?.delete_request === 1 &&
                activity?.delete_request_by === user?.role?.id &&
                'Cancel request'}
              {activity?.delete_request === 1 &&
                activity?.delete_request_by !== user?.role?.id &&
                'Delete requested by member'}
              {activity?.delete_request === 0 && 'Request delete program'}
            </MenuItem>
          )} */}
        </MenuList>
      </CustomPopover>

      {/* Members Management Dialog */}
      <MembersManagementDialog
        open={assignModalOpen}
        onClose={() => setAssignModalOpen(false)}
        activity={activity}
        allowUnassign={allowUnassign}
        onRefresh={onRefresh}
      />
    </>
  );
}
